import {useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router'
import {Face3D} from '@holition/face3d'
import {cmsClient} from 'data/cmsClient'
import {turnOffNoScroll, turnOnNoScroll} from 'appScroll'
import {Centre} from 'brand/Centre'
import {Spinner} from 'brand/Spinner'
import {lookIdsForAnswers} from './selectLooks'

const canned115LookIds = [51057, 48990, 44984]
const cannedMbpaLookIds = [52977, 52971, 52963]
const cannedLookIds = cannedMbpaLookIds

const sleep = millis => new Promise(resolve => setTimeout(resolve, millis))

const areAnswersValid = answers =>
  answers?.vibes !== undefined && answers?.occasion !== undefined

export const LooksLoading = ({selfieData, answers, setLooksData, setFaceScanResults, setRecommendedProducts}) => {
  const nav = useNavigate()

  const [startedInitFace3D, setStartedInitFace3D] = useState(false)
  const containerRef = useRef(null)
  const face3dRef = useRef(null)
  const [screenshot1, setScreenshot1] = useState()
  const [screenshot2, setScreenshot2] = useState()
  const [screenshot3, setScreenshot3] = useState()
  const [screenshots, setScreenshots] = useState({})

  const [lookIds] = useState(
    areAnswersValid(answers) ? lookIdsForAnswers(answers).slice(-3) : cannedLookIds
  )
  const setCbs = [setScreenshot1, setScreenshot2, setScreenshot3]
  const lookIdsAndSetCbs = [0, 1, 2].map(i => ({
    id: lookIds[i],
    cb: setCbs[i]
  }))

  useEffect(() => {
    const newScr = {...screenshot1, ...screenshot2, ...screenshot3}
    setScreenshots(newScr)
    setLooksData(newScr)
  }, [screenshot1, screenshot2, screenshot3, setLooksData])

  const initializeFace3d = async () => {
    try {
      face3dRef.current = await Face3D.create({
        container: containerRef.current,
      })
      await face3dRef.current.setInput(
        {type: 'image', url: selfieData, displayFit: 'cover'},
      )
    } catch (error) {
      console.error('Error initializing Face3D:', error)
      setStartedInitFace3D(false)
    }
  }

  const performScans = async () => {
    const faceScanResults = await face3dRef.current.startFaceScan({
      features: {
        ageGender: true,
        eyeCirclesBags: true,
        eyeColour: true,
        faceMeasurements: true,
        faceShape: true,
        skinHealth: true,
        skinReport: true,
        skinTone: true,
        skinType: true,
      },
      options: {iterations: 1},
      onProgress: progress => { console.log(`Face scan: ${progress}`) }
    })
    setFaceScanResults(faceScanResults)
    const recommendedProducts = await face3dRef.current.getRecommendedProducts({
      faceScanResults,
      products: cmsClient.allProducts(),
    })
    setRecommendedProducts(recommendedProducts)
    console.log('Scans done:', {faceScanResults, recommendedProducts})
  }

  const loadLookAndRender = async (id, cb) => {
    const cmsLook = cmsClient.lookById(id)
    if (cmsLook === undefined) {
      console.log('look not returned:', {id})
      return
    }
    const {look, renderProducts} = cmsLook
    const {name} = look.regions[0]?.meta
    const productCount = renderProducts.length

    await face3dRef.current.sendProducts(renderProducts)
    await sleep(500)
    const imgUrl = await face3dRef.current.takeScreenshot()

    cb({[id]: {name, imgUrl, productCount}})
  }

  const initFace3dAndRenderLooks = async () => {
    if (!face3dRef.current && !startedInitFace3D) {
      turnOnNoScroll()
      setStartedInitFace3D(true)
      await initializeFace3d()

      // await performScans().catch(e => console.log('Failed to complete scans:', e))
      for (let {id, cb} of lookIdsAndSetCbs) {
        await loadLookAndRender(id, cb)
      }
      if (!debugManualContinue) {
        await saveAndContinue()
      }
    }
  }

  useEffect(() => { initFace3dAndRenderLooks() })

  const saveAndContinue = async () => {
    await sleep(500)
    face3dRef.current?.destroy()
    turnOffNoScroll()
    nav('/consult/looks')
  }

  return (
    <div className="no-scroll">
      <Centre fullscreen column>
        <Spinner />
        <div className="size-ml loading-text">Generating your looks</div>
      </Centre>
      {debugManualContinue
        ? <button onClick={saveAndContinue}>Save and continue</button>
        : null
      }
      <div className='face3d-container'>
        <div ref={containerRef} />
        {debugRender ? lookIds.map(id =>
          <ScreenshotRenderer
            imageData={screenshots[id]}
            startRender={() => loadLookAndRender(id)}
            key={id}
          />
        ) : null}
      </div>
    </div>
  )
}

const debugManualContinue = false
const debugRender = false

const ScreenshotRenderer = ({imageData, startRender}) => imageData
  ? <img src={imageData} alt="Rendered screenshot" />
  : <div><button disabled onClick={startRender}>Render</button></div>
